import { DEFAULTS } from '@trips/shared/config';
import { estimateFlight } from '@trips/shared/flight-estimate';
import type { FormatPrefs } from '@trips/shared/format';
import { distanceMi } from '@trips/shared/geo';
import type { PlaceLookup } from '@trips/shared/items';
import { byStart } from '@trips/shared/items';
import { computeNow, type NowState } from '@trips/shared/now';
import type {
  Attachment,
  Card,
  HomeBase,
  Item,
  Perk,
  Place,
  Program,
  Redemption,
  Settings,
  Trip,
} from '@trips/shared/schema';
import { localDate } from '@trips/shared/time';
import { buildTimeline, type TimelineModel } from '@trips/shared/timeline';
import { summarizeTrip, type TripSummary } from '@trips/shared/trip-model';
import { programInfo } from '@trips/shared/valuations';
import { hereZone, now } from '../lib/clock';
import { phoneLink } from '../lib/link';
import { type AppState, derive, deriveBy } from './store';

export const DEFAULT_SETTINGS: Omit<
  Settings,
  'id' | 'rev' | 'updatedAt' | 'createdAt' | 'deleted'
> = {
  units: DEFAULTS.units,
  timeFormat: DEFAULTS.timeFormat,
  dateFormat: DEFAULTS.dateFormat,
  theme: DEFAULTS.theme,
  tripRadiusMi: DEFAULTS.tripRadiusMi,
  tripGapDays: DEFAULTS.tripGapDays,
  quietHours: { enabled: true, start: '22:00', end: '07:00', allowUrgent: true },
  notifications: {
    delays: true,
    gates: true,
    cancellations: true,
    inbound: true,
    boarding: true,
    checkIn: true,
    takeoffLanding: true,
    imports: true,
  },
  senderAllowlist: [],
  budgets: { aeroapiUsd: DEFAULTS.aeroapiBudgetUsd, anthropicUsd: DEFAULTS.anthropicBudgetUsd },
};

export const selectSettings = derive(
  (s: AppState) => [s.tables.settings.get('settings')] as const,
  (settings): Settings =>
    settings ?? {
      id: 'settings',
      rev: 0,
      updatedAt: 0,
      createdAt: 0,
      deleted: false,
      ...DEFAULT_SETTINGS,
    },
);

export const selectPrefs = derive(
  (s: AppState) => [selectSettings(s)] as const,
  (st): FormatPrefs => ({ timeFormat: st.timeFormat, dateFormat: st.dateFormat, units: st.units }),
);

export const selectPlaceLookup = derive(
  (s: AppState) => [s.tables.places] as const,
  (places): PlaceLookup =>
    (id) =>
      id ? places.get(id) : undefined,
);

/** Today's date where I am. Recomputed every minute. */
export const selectToday = derive(
  (s: AppState) => [s.minute] as const,
  (_minute) => localDate(now(), hereZone()),
);

/** Home base in effect today, with its place. */
export const selectHome = derive(
  (s: AppState) => [s.tables.homeBases, s.tables.places, selectToday(s)] as const,
  (homeBases, places, today): { base: HomeBase; place: Place } | null => {
    const active = [...homeBases.values()]
      .filter((h) => !h.deleted && h.effectiveFrom <= today)
      .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
    const place = active ? places.get(active.placeId) : undefined;
    return active && place ? { base: active, place } : null;
  },
);

export const selectItemsByTrip = derive(
  (s: AppState) => [s.tables.items] as const,
  (items): Map<string, Item[]> => {
    const out = new Map<string, Item[]>();
    for (const i of items.values()) {
      if (i.deleted || !i.tripId) continue;
      const list = out.get(i.tripId) ?? [];
      list.push(i);
      out.set(i.tripId, list);
    }
    for (const list of out.values()) list.sort(byStart);
    return out;
  },
);

export const selectLiveItems = derive(
  (s: AppState) => [s.tables.items] as const,
  (items): Item[] => [...items.values()].filter((i) => !i.deleted).sort(byStart),
);

export const selectTripSummaries = derive(
  (s: AppState) =>
    [
      s.tables.trips,
      selectItemsByTrip(s),
      selectPlaceLookup(s),
      selectToday(s),
      selectHome(s),
    ] as const,
  (trips, byTrip, places, today, home): TripSummary[] =>
    [...trips.values()]
      .filter((t) => !t.deleted)
      .map((t) => summarizeTrip(t, byTrip.get(t.id) ?? [], places, today, home?.place.city))
      .sort((a, b) => (a.startDate ?? '9999').localeCompare(b.startDate ?? '9999')),
);

export const selectTripLists = derive(
  (s: AppState) => [selectTripSummaries(s)] as const,
  (all) => ({
    current: all.filter((t) => t.phase === 'current'),
    upcoming: all.filter((t) => t.phase === 'upcoming'),
    past: all.filter((t) => t.phase === 'past').reverse(),
  }),
);

export const selectTripSummary = (s: AppState, id: string): TripSummary | undefined =>
  selectTripSummaries(s).find((t) => t.id === id);

export const selectTrip = (s: AppState, id: string): Trip | undefined => s.tables.trips.get(id);

export const selectTimeline = deriveBy(
  (s: AppState, tripId: string) =>
    [
      selectItemsByTrip(s).get(tripId),
      s.tables.trips.get(tripId),
      selectPlaceLookup(s),
      selectPrefs(s),
      selectToday(s),
      selectHome(s),
      selectSettings(s).tripRadiusMi,
    ] as const,
  (_id, items, trip, places, prefs, today, home, radius): TimelineModel =>
    buildTimeline(items ?? [], {
      places,
      prefs,
      today,
      startDate: trip?.startDate ?? null,
      endDate: trip?.endDate ?? null,
      home:
        home?.place.lat != null && home.place.lon != null
          ? { lat: home.place.lat, lon: home.place.lon, radiusMi: radius }
          : null,
    }),
);

export const selectNow = derive(
  (s: AppState) =>
    [
      selectLiveItems(s),
      selectTripSummaries(s),
      selectItemsByTrip(s),
      selectPlaceLookup(s),
      s.tables.flightLive,
      s.minute,
    ] as const,
  (items, trips, byTrip, places, lives, _minute): NowState =>
    computeNow({
      now: now(),
      // In the air from takeoff as the phone works it out, so a late flight stays on the ground.
      moveTimes: (i) => {
        if (i.type !== 'flight' || i.startAt == null) return null;
        const e = estimateFlight(
          { startAt: i.startAt, endAt: i.endAt },
          lives.get(i.id),
          now(),
          undefined,
          phoneLink(),
        );
        return { depart: e.off, arrive: e.in };
      },
      items,
      trips,
      itemsByTrip: byTrip,
      places,
      today: localDate(now(), hereZone()),
    }),
);

export const selectAttachmentsByOwner = derive(
  (s: AppState) => [s.tables.attachments] as const,
  (atts): Map<string, Attachment[]> => {
    const out = new Map<string, Attachment[]>();
    for (const a of atts.values()) {
      if (a.deleted) continue;
      const list = out.get(a.ownerId) ?? [];
      list.push(a);
      out.set(a.ownerId, list);
    }
    for (const list of out.values())
      list.sort((a, b) => (a.takenAt ?? a.createdAt) - (b.takenAt ?? b.createdAt));
    return out;
  },
);

/** Photos on a trip and on its plans, oldest first. */
/** True when a place is within the home radius. */
export function isNearHome(
  place: Place | undefined,
  home: Place | undefined,
  radiusMi: number,
): boolean {
  if (
    !place ||
    !home ||
    place.lat == null ||
    place.lon == null ||
    home.lat == null ||
    home.lon == null
  )
    return false;
  return (
    distanceMi({ lat: place.lat, lon: place.lon }, { lat: home.lat, lon: home.lon }) <= radiusMi
  );
}

export interface WalletData {
  programs: Program[];
  cards: Card[];
  perks: Perk[];
  redemptions: Redemption[];
}

/** Points, cards, perks and redemptions; programs by value, newest redemptions first. */
export const selectWallet = derive(
  (s: AppState) =>
    [s.tables.programs, s.tables.cards, s.tables.perks, s.tables.redemptions] as const,
  (programs, cards, perks, redemptions): WalletData => {
    const live = <T extends { deleted: boolean }>(m: Map<string, T>) =>
      [...m.values()].filter((r) => !r.deleted);
    const worth = (p: Program) => p.balance * (programInfo(p.programId)?.typicalCpp ?? 1);
    return {
      programs: live(programs).sort((a, b) => worth(b) - worth(a)),
      cards: live(cards).sort((a, b) => b.annualFee - a.annualFee || a.name.localeCompare(b.name)),
      perks: live(perks).sort((a, b) =>
        (a.expiresOn ?? '9999').localeCompare(b.expiresOn ?? '9999'),
      ),
      redemptions: live(redemptions).sort((a, b) => b.on.localeCompare(a.on)),
    };
  },
);
